import {
  Component,
  OnInit,
  inject,
  Injector,
  HostBinding,
  input,
  signal,
  effect,
  type WritableSignal,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { ICONS } from '@constants';
import type { IconName, CssColor } from '@appTypes';

@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [],
  templateUrl: './icon.html',
  styles: `
    :host {
      display: inline-block;
    }
  `,
})
export class Icon implements OnInit {
  // Dependencias
  private readonly injector = inject(Injector);
  private readonly domSanitizer = inject(DomSanitizer);

  // Inputs
  $icon = input.required<IconName>({ alias: 'icon' });
  $color = input<'original' | CssColor, 'original' | CssColor | undefined>('--primary-secondary',
    { alias: 'color', transform: v => v !== undefined ? v : '--primary-secondary' });
  $size = input<string | undefined>(undefined, { alias: 'size' });
  $scale = input<number>(1, { alias: 'scale' });
  $rotation = input<number>(0, { alias: 'rotation' });
  $rotationDelay = input<number>(0, { alias: 'rotationDelay' });
  $viewBoxAdjustmentX = input<number>(0, { alias: 'viewBoxAdjustmentX' });
  $viewBoxAdjustmentY = input<number>(0, { alias: 'viewBoxAdjustmentY' });

  // Signals / atributos
  $width = signal<string | undefined>(undefined);
  $height = signal<string | undefined>(undefined);
  $defaultWidth = signal<string>('35px');
  $defaultHeight = signal<string>('35px');
  $svgHtml: WritableSignal<SafeHtml | null> = signal(null);

  // Bindings
  @HostBinding('style.scale')
  get hostScale(): string | undefined {
    return this.$scale() > 0 ? undefined : '-1 1';
  }

  @HostBinding('style.rotate')
  get hostRotate(): string | undefined {
    return this.$rotation() === 0 ? undefined : `${ this.$rotation() }deg`;
  }

  @HostBinding('style.transition')
  get hostTransition(): string | undefined {
    return `${ this.$rotationDelay() === 0 ? undefined : `rotate ${ this.$rotationDelay() }ms ease-in-out` }`.trim();
  }

  ngOnInit() {
    this.effectsRunner();
  }

  // Ejecuta los effects necesarios para cambiar las propiedades del ícono
  private effectsRunner() {
    effect(() => {
      // Obtener width y height proporcionados por el usuario
      let width: string | undefined = undefined;
      let height: string | undefined = undefined;

      let size: string | undefined = this.$size()?.trim();
      if (size !== undefined && size !== '') {
        size = size.replaceAll(';', '').replace(/(?<=\S)\s+(?=\S)/g, ' ');
        const sizes = size.split(' ');
        if (sizes.length) {
          width = sizes[0];
          height = sizes.length === 1 ? width : sizes[1];
        }
      }

      this.$width.set(width);
      this.$height.set(height);
      this.$svgHtml.set(this.domSanitizer.bypassSecurityTrustHtml(
        this.getSVGCode(this.$icon(), this.$color(), width, height, Math.abs(this.$scale()), this.$viewBoxAdjustmentX(), this.$viewBoxAdjustmentY()),
      ));
    }, { injector: this.injector });
  }

  // Obtiene, mdifica y devuelve el código SVG del ícono indicado
  private getSVGCode(
    iconName: IconName,
    color: 'original' | CssColor,
    width: string | undefined,
    height: string | undefined,
    scale: number,
    viewBoxAdjustmenX: number,
    viewBoxAdjustmenY: number,
  ): string {
    const icon = ICONS[iconName];
    let iconPaths = icon.paths;
    if (color !== 'original')
      iconPaths = icon.paths.replace(/(?<=fill=").+?(?=")/gi, `var(${ color })`);
    // Obtener el ancho del ícono
    if (width === undefined)
      if (icon.width === '')
        width = this.$defaultWidth();
      else {
        width = icon.width;
        this.$defaultWidth.set(icon.width);
      }
    if (/^\d+(?:\.\d+)?$/.test(width.trim()))
      width += 'px';

    const widthMatch = width.trim().match(/(\d+(?:\.\d+)?)(\D+)/);
    if (widthMatch)
      width = `${ Number(widthMatch[1]) * scale }${ widthMatch[2] }`;

    // Obtener el alto del ícono
    if (height === undefined)
      if (icon.height === '')
        height = this.$defaultHeight();
      else {
        height = icon.height;
        this.$defaultHeight.set(icon.height);
      }
    if (/^\d+(?:\.\d+)?$/.test(height.trim()))
      height += 'px';

    const heightMatch = height.trim().match(/(\d+(?:\.\d+)?)(\D+)/);
    if (heightMatch)
      height = `${ Number(heightMatch[1]) * scale }${ heightMatch[2] }`;

    let viewBox = icon.viewBox;
    if (viewBoxAdjustmenX !== 0 || viewBoxAdjustmenY !== 0) {
      const viewBoxMatch = viewBox.match(/\d+(?:\.\d+)?/g);
      if (viewBoxMatch)
        viewBox = `${ Number(viewBoxMatch[0]) + (viewBoxAdjustmenX / 2) } ${ Number(viewBoxMatch[1]) + (viewBoxAdjustmenY / 2) } ${ Number(viewBoxMatch[2]) - viewBoxAdjustmenX } ${ Number(viewBoxMatch[3]) - viewBoxAdjustmenY }`;
    }

    let svgCode: string = `<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" width="${ width }" height="${ height }"`;
    svgCode += `${ !viewBox ? '' : ` viewBox="${ viewBox }"` }>${ iconPaths }</svg>`;
    return svgCode;
  }
}
